'use client';
// Reviews V5 (default everywhere, 30/09/26): "(5)" next to the reviewer name + tooltip "דני כתב/ה 5 המלצות ב-Google".
// Opens on mouse hover, keyboard focus and tap (a tap toggles); Escape / outside tap / scroll closes. Accessible: a real <button>
// with aria-describedby.
// bughunter 29/09/26: (1) at 390 the tooltip ran up to 17px off the viewport's left edge and was clipped by the card's
// overflow:hidden → it is now position:fixed, measured on open and clamped 8px inside the viewport, capped at 100vw-32px (wraps),
// z-index 60 = above the floating avatar / WhatsApp buttons (45) and the sticky bars (40). (2) a tap never opened it — the tap's
// synthetic mouseleave closed it at once → hover is pointer events filtered to pointerType 'mouse' only; touch uses the click toggle.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// V5 (30/09/26, category listing cards): the tip is PORTALLED to <body> — inside the reviews strip an animated (transform) ancestor
// made position:fixed relative to the strip column, so at 390 the tip opened off-screen. Portal = fixed is the viewport again.

const EDGE = 8;

export default function ReviewerCountTip({ short, tip }: { short: string; tip: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const press = useRef({ open: false, mouse: false, pinned: false });
  // bughunter 30/09/26 (1280): a CLICK-opened tip closed on mouseleave. Click/tap = PINNED: it stays until an outside click, Escape,
  // scroll or a second click. Hover-open (not pinned) still closes on mouseleave; keyboard focus opens, blur closes unless pinned.
  const pinned = useRef(false);
  const close = () => { pinned.current = false; setOpen(false); };
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node) && !tipRef.current?.contains(e.target as Node)) close(); };
    // Escape closes ONLY the open tip: window capture runs before any modal's Escape handler (bughunter 01/10: Esc shut the admin reviews modal too)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); e.preventDefault(); close(); } };
    // close only when the scrolled element CONTAINS the button (or the page itself) — an unrelated auto-scrolling strip fires scroll events non-stop (bughunter 30/09: tooltip shut the instant it opened)
    const onMove = (e?: Event) => { const t = e?.target; if (!t || t === document || t === window || (t instanceof Node && btnRef.current && t.contains(btnRef.current))) close(); };
    document.addEventListener('pointerdown', onDoc); window.addEventListener('keydown', onKey, { capture: true });
    // capture on document = a scroll of ANY ancestor (the reviews strip, a horizontal carousel) closes it, not only the window
    document.addEventListener('scroll', onMove, { capture: true, passive: true }); window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onDoc); window.removeEventListener('keydown', onKey, { capture: true });
      document.removeEventListener('scroll', onMove, { capture: true }); window.removeEventListener('resize', onMove);
    };
  }, [open]);

  // Measure after the tooltip is laid out: start at the button's inline-start edge (right, in RTL), then clamp into the viewport.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const b = btnRef.current?.getBoundingClientRect();
    const t = tipRef.current?.getBoundingClientRect();
    if (!b || !t) return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const rtl = getComputedStyle(btnRef.current!).direction === 'rtl';
    let left = rtl ? b.right - t.width : b.left;
    left = Math.max(EDGE, Math.min(left, vw - EDGE - t.width));
    setPos({ top: b.bottom + 6, left });
  }, [open]);

  // digits only in LTR isolation (RTL numbers law); the Hebrew word stays in the RTL flow
  const parts = short.match(/^(\d+)\s(.+)$/);
  const shown = open && !!pos;
  return (
    <span ref={ref} className="relative inline-block"
      onPointerEnter={e => { if (e.pointerType === 'mouse') setOpen(true); }}
      onPointerLeave={e => { if (e.pointerType === 'mouse' && !pinned.current) setOpen(false); }}>
      <button ref={btnRef} type="button" aria-describedby={id} aria-expanded={open}
        // touch: focus fires BEFORE click, so the toggle reads the state from pointerdown (a tap opens, a 2nd tap closes);
        // mouse: hover already opened it — a click PINS it (a 2nd click unpins + closes); a tap pins/closes the same way
        onPointerDown={e => { press.current = { open, mouse: e.pointerType === 'mouse', pinned: pinned.current }; }}
        onClick={e => { const wasOpen = e.detail === 0 ? pinned.current /* keyboard Enter/Space */ : press.current.mouse ? press.current.pinned : press.current.open; if (wasOpen) close(); else { pinned.current = true; setOpen(true); } }}
        onFocus={() => setOpen(true)} onBlur={() => { if (!pinned.current) setOpen(false); }}
        className="text-xs font-normal align-baseline whitespace-nowrap"
        style={{ color: '#64748b', background: 'none', border: 0, padding: '6px 2px', margin: '-6px 0', cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
        ({parts ? <><bdi dir="ltr">{parts[1]}</bdi> {parts[2]}</> : /^\d+$/.test(short) ? <bdi dir="ltr">{short}</bdi> : short})
      </button>
      {mounted && createPortal(
      <span ref={tipRef} id={id} role="tooltip" dir="rtl"
        className="text-xs rounded-lg px-2.5 py-1.5"
        style={{ position: 'fixed', zIndex: 10001,   /* 01/10/26 bughunter: above every admin modal (reviews modal 9999, ProEditModal 9000) — no shared z-layer constant in the repo */ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: 'max-content',
          maxInlineSize: 'calc(100vw - 32px)', whiteSpace: 'normal', lineHeight: 1.4,
          background: '#0f172a', color: '#fff', boxShadow: '0 6px 20px rgba(15,23,42,.25)',
          opacity: shown ? 1 : 0, visibility: open ? 'visible' : 'hidden', transition: 'opacity .12s', pointerEvents: 'none' }}>
        {tip}
      </span>,
        document.body)}
    </span>
  );
}
